import { io, type Socket } from "socket.io-client";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { mocksEnabled } from "@/portals/physician/api/transport";
import { hardenSocket } from "@/lib/socketRecovery";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

let socket: Socket | null = null;

function getOrCreateSocket(): Socket {
  if (socket) return socket;
  socket = io(BASE_URL || undefined, {
    autoConnect: false,
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  // Without this, a socket that never connects (wrong VITE_API_URL, a dev
  // proxy missing a /socket.io rule, a corporate network blocking
  // WebSockets, the backend being down, ...) fails completely silently —
  // it just retries forever with zero visible signal anywhere, which is
  // exactly what made every prior instance of "live updates aren't live"
  // hard to diagnose. These logs are the first thing to check from now on.
  // Was disabled (2026-09-09) for "repeated connect failures/CORS noise against
  // the ngrok tunnel in local dev" — every retry logged a full error. Now only
  // the first failure of a streak is logged; a successful connect resets it.
  let failureLogged = false;
  socket.on("connect", () => {
    failureLogged = false;
    console.info("[realtime] connected", socket?.id);
  });
  socket.on("disconnect", (reason) => {
    console.warn("[realtime] disconnected:", reason);
  });
  socket.on("connect_error", (err) => {
    if (failureLogged) return;
    failureLogged = true;
    console.error(
      "[realtime] failed to connect — live updates (notifications, dashboards) will not arrive until this is fixed. " +
        "Check: is the backend running, does VITE_API_URL point at it, and (local dev only) does vite.config.ts proxy /socket.io. Error:",
      err.message,
    );
  });
  // Retries a refused reconnect and announces when events may have been missed.
  hardenSocket(socket, () => Boolean(usePatientAuthStore.getState().accessToken));
  return socket;
}

export function getSocket(): Socket {
  return getOrCreateSocket();
}

export function syncAuth(token: string | null): void {
  if (mocksEnabled()) return; // no Socket.IO server behind the mocks
  const s = getOrCreateSocket();
  if (!token) {
    s.disconnect();
    return;
  }
  // Sets what the NEXT connect (including any future auto-reconnect after a
  // network blip) authenticates with, not just this one.
  s.auth = { token };
  if (s.connected) {
    // Already connected under a possibly-stale token (proactive REST
    // refresh happens ~60s before expiry — see createAuthStore.ts) — re-auth
    // in place rather than dropping the connection (see the server's
    // "authenticate" handler in app/core/realtime/server.py).
    s.emit("authenticate", { token });
  } else {
    s.connect();
  }
}

let lastToken: string | null = null;
usePatientAuthStore.subscribe((state) => {
  if (state.accessToken === lastToken) return;
  lastToken = state.accessToken;
  syncAuth(state.accessToken);
});

// Covers a token that's already present the moment this module first
// executes — the subscribe() above only fires on subsequent changes, so a
// page that mounts after silentRestore() already resolved needs this too.
const initialToken = usePatientAuthStore.getState().accessToken;
if (initialToken) syncAuth(initialToken);
